import { apiClient } from './apiClient';
import type { SupportCategory } from './supportService';

export type DepartmentKey = Exclude<SupportCategory, 'technical'>;

export interface DepartmentMember {
  teamwork_person_id: number;
  name: string;
  email: string | null;
  role_name: string;
  department: DepartmentKey;
  is_lead: boolean;
  is_active: boolean;
  slack_user_id: string | null;
  slack_user_id_override: string | null;
  effective_slack_id: string | null;
  synced_at: string | null;
  // Core overrides (B3). Optional: an older backend does not send them.
  source?: 'teamwork';
  /** Removed from this department in Core (listed under Removed instead). */
  excluded?: boolean;
  /** Made lead in Core (on top of Teamwork's own lead flag, `is_lead`). */
  lead_in_core?: boolean;
  effective_is_lead?: boolean;
}

/** include: add the person (is_lead = also lead). exclude: remove a Teamwork
 *  member. lead: make a Teamwork member lead while they hold the role. */
export type OverrideMode = 'include' | 'exclude' | 'lead';

/** An admin's include/exclude of one person (by email) in one department. */
export interface DepartmentOverride {
  id: string;
  department: DepartmentKey;
  email: string;
  display_name: string;
  mode: OverrideMode;
  is_lead: boolean;
  slack_user_id: string | null;
  slack_lookup_id: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** Someone added to a department in Core (an include with no Teamwork row there). */
export interface AddedPerson extends DepartmentOverride {
  name: string;
  source: 'core';
  effective_slack_id: string | null;
}

/** Who a department's tickets mention, after overrides. */
export interface EffectivePerson {
  name: string;
  email: string | null;
  is_lead: boolean;
  slack_id: string | null;
  source: 'teamwork' | 'core';
}

export interface Department {
  key: DepartmentKey;
  label: string;
  escalation_slack_id: string | null;
  members: DepartmentMember[];
  effective?: EffectivePerson[];
  added?: AddedPerson[];
  removed?: DepartmentOverride[];
  /** 'lead' overrides for people no longer in this Teamwork department: no effect. */
  dormant_leads?: DepartmentOverride[];
}

export interface OverrideInput {
  email: string;
  display_name: string;
  mode: OverrideMode;
  is_lead?: boolean;
  slack_user_id?: string | null;
}

export interface DepartmentsResponse {
  routing_enabled: boolean;
  departments: Department[];
}

export interface DepartmentSyncResult {
  ok: boolean;
  roles?: number;
  active_members?: number;
  created?: number;
  updated?: number;
  deactivated?: number;
  unmapped_roles?: string[];
  slack_resolved?: number;
  slack_not_found?: number;
  lookups_stopped?: boolean;
  error?: string;
}

/** Admin API for support-ticket department routing (super_admin /
 *  platform_owner only — the backend enforces it). */
export const supportDepartmentsService = {
  async list(): Promise<DepartmentsResponse> {
    const { data } = await apiClient.get<DepartmentsResponse>('/support/departments');
    return data;
  },

  /** Refresh the member cache from Teamwork. A failed sync is a 502 whose
   *  `detail` is the sync result ({ ok: false, error, status }). */
  async sync(): Promise<DepartmentSyncResult> {
    const { data } = await apiClient.post<DepartmentSyncResult>('/support/departments/sync');
    return data;
  },

  /** Set (or clear with null) a member's Slack id override. */
  async setOverride(teamworkPersonId: number, slackUserId: string | null) {
    const { data } = await apiClient.put(
      `/support/departments/members/${teamworkPersonId}/slack-override`,
      { slack_user_id: slackUserId },
    );
    return data;
  },

  /** Add a person to a department (include, optionally lead) or remove a
   *  Teamwork member from it (exclude). One override per department + email. */
  async upsertDepartmentOverride(
    department: DepartmentKey,
    input: OverrideInput,
  ): Promise<DepartmentOverride> {
    const { data } = await apiClient.put<DepartmentOverride>(
      `/support/departments/${department}/overrides`,
      input,
    );
    return data;
  },

  /** Delete an override: the person reverts to what Teamwork says. */
  async deleteDepartmentOverride(department: DepartmentKey, email: string) {
    const { data } = await apiClient.delete<{ deleted: boolean; department: string; email: string }>(
      `/support/departments/${department}/overrides/${encodeURIComponent(email)}`,
    );
    return data;
  },

  /** Set (or clear with null) a department's escalation contact. */
  async setEscalation(department: DepartmentKey, slackUserId: string | null) {
    const { data } = await apiClient.put(`/support/departments/${department}/escalation`, {
      slack_user_id: slackUserId,
    });
    return data;
  },
};
